import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Search, MapPin, Anchor, Globe2, Sparkles, X } from 'lucide-react';
import { useAtlasSites, useDiveSiteSearch, useMyDiveSites } from '@/data/dive-sites';
import { MapView, type MapPoint } from '@/components/map/MapView';
import { StaticMap } from '@/components/map/StaticMap';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { formatGermanDate } from '@/lib/format';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { cn } from '@/lib/utils';
import type { DiveSite } from '@/types/api';

const SUGGESTIONS = ['Hurghada', 'Red Sea', 'Malta', 'Cenote', 'Pemuteran', 'Bali', 'Komodo'];

const SOURCE_LABELS: Record<string, string> = {
  user:        'Eigene',
  osm:         'OSM',
  opendeepmap: 'ODM',
  dive_vibe:   'DiveVibe',
  imported:    'Import',
};

type MapFilter = 'world' | 'mine';

export function DiveSitesListPage() {
  useDocumentTitle('Tauchplätze');
  const [q, setQ]               = useState('');
  const [mapFilter, setMapFilter] = useState<MapFilter>('world');
  const isSearching = q.trim().length >= 2;

  const search = useDiveSiteSearch(q, isSearching);
  const mine   = useMyDiveSites();
  const atlas  = useAtlasSites();

  const sourceItems: DiveSite[] = isSearching ? (search.data?.items ?? []) : (mine.data?.items ?? []);

  // Search results / "mine" cards still drive the bottom grid + the legacy map data.
  const searchOrMinePoints = useMemo<MapPoint[]>(
    () =>
      sourceItems
        .filter((s) => s.geoLatitude != null && s.geoLongitude != null)
        .map((s) => ({
          id:   s.id,
          lat:  s.geoLatitude!,
          lng:  s.geoLongitude!,
          href: `/sites/${s.id}`,
          tooltip: {
            title:    s.title,
            subtitle: s.regionPathStr ?? undefined,
            badge:    s.siteType ?? undefined,
            metrics:  [
              { label: 'Quelle', value: SOURCE_LABELS[s.source] ?? s.source },
              ...(s.userDiveCount != null
                ? [{ label: 'Eigene Logs', value: String(s.userDiveCount) }]
                : s.maxDepthMeters != null
                  ? [{ icon: 'depth' as const, label: 'Tiefe', value: `${s.maxDepthMeters} m` }]
                  : []),
              ...(s.lastDive
                ? [{ icon: 'date' as const, label: 'Zuletzt', value: formatGermanDate(s.lastDive) }]
                : []),
            ],
          },
        })),
    [sourceItems],
  );

  // World atlas points – all coord-bearing sites, optionally filtered to mine=true.
  const atlasPoints = useMemo<MapPoint[]>(() => {
    const all = atlas.data?.items ?? [];
    const filtered = mapFilter === 'mine' ? all.filter((s) => s.mine) : all;
    return filtered.map((s) => ({
      id:   s.id,
      lat:  s.lat,
      lng:  s.lng,
      href: `/sites/${s.id}`,
      tooltip: {
        title:    s.title,
        subtitle: SOURCE_LABELS[s.source] ?? s.source,
        badge:    s.mine ? 'eigene' : (s.siteType ?? undefined),
      },
    }));
  }, [atlas.data, mapFilter]);

  // Which set of points the big map shows: search/mine results override the atlas.
  const showingAtlas = !isSearching && atlasPoints.length > 0 && mapFilter !== 'mine';
  const mapPoints = isSearching || (atlasPoints.length === 0 && sourceItems.length > 0)
    ? searchOrMinePoints
    : atlasPoints;

  // World atlas would otherwise fitBounds across all 18 k+ points → useless
  // zoomed-out world view dominated by the Eurasia mass. Instead fit a
  // Pacific-spanning frame (continental US → Hawaii) so the user lands inside
  // dense dive-territory and can pan from there.
  const atlasDefaultBounds = useMemo(
    () => ({
      sw: { lat: 15,  lng: -160 }, // Hawaii latitude, west of Honolulu
      ne: { lat: 50,  lng:  -65 }, // northern US, just east of Maine
    }),
    [],
  );

  const mineCount = atlas.data?.items.filter((s) => s.mine).length ?? 0;

  const heading = isSearching
    ? `${sourceItems.length} ${sourceItems.length === 1 ? 'Treffer' : 'Treffer'} für „${q.trim()}"`
    : 'Deine Tauchplätze';

  const subheading = isSearching
    ? undefined
    : mine.data
      ? `${mine.data.items.length} Plätze, an denen du schon getaucht bist.`
      : undefined;

  return (
    <div className="space-y-6 p-4 sm:p-6 md:p-8">
      <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-ocean-700 via-ocean-600 to-ocean-500 p-4 sm:p-6 md:p-8 text-white shadow-lg">
        <div aria-hidden className="pointer-events-none absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-coral-500/20 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -left-12 -top-12 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
        <div className="relative">
          <div className="flex items-center gap-2 text-sm text-ocean-100">
            <Globe2 className="h-4 w-4" /> Über 20.000 Tauchplätze weltweit
          </div>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight md:text-4xl">Tauchplätze entdecken</h1>
          <p className="mt-1 max-w-xl text-sm text-ocean-100">
            Such global, oder bleib bei deinen besuchten Plätzen unten. Neue legst du direkt im Tauchgang-Wizard an.
          </p>

          <div className="relative mt-5 max-w-2xl">
            <Search className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-ocean-100" />
            <input
              type="search"
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              autoFocus
              placeholder="Site, Region, Land…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className={cn(
                'h-12 w-full rounded-xl bg-white/15 pl-12 pr-12 text-base text-white placeholder:text-ocean-100',
                'shadow-inner ring-1 ring-white/30 backdrop-blur',
                'focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60',
              )}
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-ocean-100 hover:bg-white/15"
                aria-label="Suche leeren"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {!isSearching && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-ocean-100">Probier:</span>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setQ(s)}
                  className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white ring-1 ring-white/30 backdrop-blur transition hover:bg-white/20"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {(mapPoints.length > 0 || atlas.isLoading) && (
        <Card>
          <CardContent className="p-0">
            <div className="relative">
              {/* Toggle pill – only meaningful when we're showing the world atlas, not a search. */}
              {!isSearching && atlas.data && (
                <div className="pointer-events-auto absolute right-3 top-3 z-10 inline-flex overflow-hidden rounded-full bg-white/95 p-0.5 text-xs font-medium shadow-md ring-1 ring-ocean-200">
                  <button
                    type="button"
                    onClick={() => setMapFilter('world')}
                    className={cn(
                      'rounded-full px-3 py-1 transition',
                      mapFilter === 'world'
                        ? 'bg-ocean-700 text-white'
                        : 'text-ocean-700 hover:bg-ocean-50',
                    )}
                  >
                    Weltweit · {atlas.data.items.length}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMapFilter('mine')}
                    className={cn(
                      'rounded-full px-3 py-1 transition',
                      mapFilter === 'mine'
                        ? 'bg-coral-500 text-white'
                        : 'text-coral-600 hover:bg-coral-50',
                    )}
                    disabled={mineCount === 0}
                  >
                    Meine · {mineCount}
                  </button>
                </div>
              )}
              {atlas.isLoading && !isSearching ? (
                <div className="grid place-items-center" style={{ height: 360 }}>
                  <Spinner className="h-8 w-8" />
                </div>
              ) : (
                <MapView
                  points={mapPoints}
                  height={360}
                  rounded={false}
                  defaultBounds={showingAtlas ? atlasDefaultBounds : undefined}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <header className="flex items-end justify-between gap-2 px-1">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            {!isSearching && <Sparkles className="h-4 w-4 text-coral-500" />}
            {heading}
          </h2>
          {subheading && (
            <p className="text-sm text-[var(--muted-foreground)]">{subheading}</p>
          )}
        </div>
      </header>

      {(isSearching ? search.isFetching : mine.isLoading) && (
        <div className="grid place-items-center py-16"><Spinner className="h-8 w-8" /></div>
      )}

      {!isSearching && mine.data && mine.data.items.length === 0 && (
        <Card>
          <CardContent className="py-14 text-center">
            <MapPin className="mx-auto h-10 w-10 text-ocean-300" />
            <p className="mt-3 text-sm text-[var(--muted-foreground)]">
              Du hast noch keinen Tauchplatz besucht.
              <br />
              Such oben global oder leg deinen ersten Tauchgang an.
            </p>
            <Link
              to="/logs/new"
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-md bg-ocean-700 px-4 text-sm font-medium text-white hover:bg-ocean-600"
            >
              Neuer Tauchgang
            </Link>
          </CardContent>
        </Card>
      )}

      {isSearching && search.data && sourceItems.length === 0 && !search.isFetching && (
        <Card>
          <CardContent className="py-14 text-center">
            <MapPin className="mx-auto h-10 w-10 text-[var(--muted-foreground)]" />
            <p className="mt-3 text-sm text-[var(--muted-foreground)]">
              Keine Treffer für „<span className="font-medium text-[var(--foreground)]">{q.trim()}</span>".
              <br />
              Du kannst den Platz direkt beim Loggen als neue Site anlegen.
            </p>
          </CardContent>
        </Card>
      )}

      {sourceItems.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {sourceItems.map((site) => {
            const hasCoords = site.geoLatitude != null && site.geoLongitude != null;
            return (
              <Link key={site.id} to="/sites/$id" params={{ id: String(site.id) }} className="group block">
                <Card className="h-full overflow-hidden border-transparent transition group-hover:shadow-xl group-hover:ring-2 group-hover:ring-ocean-200">
                  <div className="relative h-28 bg-gradient-to-br from-ocean-700 to-ocean-900">
                    {hasCoords ? (
                      <StaticMap
                        lat={site.geoLatitude!}
                        lng={site.geoLongitude!}
                        zoom={10}
                        width={480}
                        height={120}
                      />
                    ) : (
                      // No coords → soft pin watermark so the header isn't empty
                      <div className="absolute inset-0 grid place-items-center">
                        <MapPin className="h-10 w-10 text-white/15" strokeWidth={1.25} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-ocean-900/80 via-ocean-900/30 to-transparent" />
                    <div className="absolute right-3 top-3 flex gap-1.5">
                      {site.source !== 'user' && (
                        <span className="rounded-full bg-white/85 px-2 py-0.5 text-[10px] font-medium text-ocean-800 shadow-sm backdrop-blur">
                          {SOURCE_LABELS[site.source] ?? site.source}
                        </span>
                      )}
                      {site.siteType && (
                        <span className="rounded-full bg-coral-500/90 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white shadow-sm backdrop-blur">
                          {site.siteType.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-3">
                      <h3 className="line-clamp-2 text-sm font-semibold leading-tight text-white drop-shadow">{site.title}</h3>
                    </div>
                  </div>
                  <CardContent className="space-y-2 pt-3">
                    {site.regionPathStr && (
                      <p className="flex items-center gap-1 truncate text-xs text-[var(--muted-foreground)]">
                        <MapPin className="h-3 w-3 shrink-0 text-ocean-700" />
                        <span className="truncate">{site.regionPathStr}</span>
                      </p>
                    )}
                    <div className="flex items-center justify-between text-[11px] text-[var(--muted-foreground)]">
                      <span className="flex items-center gap-1">
                        <Anchor className="h-3 w-3" />
                        {site.userDiveCount != null
                          ? `${site.userDiveCount} eigene Logs`
                          : `${site.diveCount ?? 0} Logs`}
                      </span>
                      {site.lastDive && <span>{formatGermanDate(site.lastDive)}</span>}
                      {!site.lastDive && site.maxDepthMeters != null && <span>bis {site.maxDepthMeters} m</span>}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
